import type { XYPosition } from 'react-flow-renderer';
import type { ContainerNode, SnippetNode, WorkflowNode } from '../../nodes';
import {
  getLayoutContainerId,
  isCollapsedSnippetNode,
  isContainerNode,
  isExpandedSnippetNode,
  isInputAttributeNode,
  isRiskCalculationNode,
  isSnippetInternalChildNode,
  isSnippetNode,
} from '../../nodes';
import {
  CANVAS_HORIZONTAL_PADDING,
  CANVAS_VERTICAL_PADDING,
  COLUMN_SPACING,
  XL_NODE_FIRST_ROW_HOIST,
  LEFT_CHILD_PADDING,
  NODE_HEIGHT,
  NODE_WIDTH,
  ROW_SPACING,
  EXPANDED_SNIPPET_X_OFFSET,
  EXPANDED_SNIPPET_Y_OFFSET,
} from '../constants';
import type { MaxPathLengthMap } from '../types';
import type {
  Allocation,
  NodeColumn,
  NodeDimensions,
  NodeMatrix,
  NodeMatrixEntry,
  NodePosition,
  SnippetSpan,
} from './types';
import { defaultNodeMatrixEntry } from './constants';
import { isActionIdOfType } from 'flow/service/actions';
import { ActionType } from 'flow/store/engine/constants';
import type { PublicId } from 'flow/store/engine/types';

/**
 * convert column and row numbers to pixels using the predefined dimensions
 */
export function getNodePosition(args: { row: number; column: number }): WorkflowNode['position'] {
  const { row, column } = args;

  const x = CANVAS_HORIZONTAL_PADDING + (column * (NODE_WIDTH + COLUMN_SPACING));
  const y = CANVAS_VERTICAL_PADDING + (row * (NODE_HEIGHT + ROW_SPACING));

  return { x, y };
}

function offsetFolderChildNode(position: XYPosition) {
  return { x: position.x + LEFT_CHILD_PADDING, y: position.y };
}

export function getNextAvailableRowIndex(
  column: NodeColumn,
  node: WorkflowNode,
) {
  const requiresOwnership = (getLayoutContainerId(node) !== null) || isContainerNode(node);
  const comparator = requiresOwnership
    ? (allocation: Allocation) => isReservedForNode(allocation, node)
    : isFree;

  for (let rowIndex = 0; rowIndex < column.length; rowIndex += 1) {
    if (comparator(column[rowIndex].allocation)) {
      return rowIndex;
    }
  }

  return -1;
}

function maxOccurrences(values: Iterable<number>): number {
  const frequencyMap = new Map<number, number>();
  let max = 0;

  for (const v of values) {
    const next = (frequencyMap.get(v) ?? 0) + 1;
    frequencyMap.set(v, next);
    if (next > max) max = next;
  }

  return max;
}

export function getDimensions(
  maxPathLengthMap: MaxPathLengthMap,
): NodeDimensions {
  // the furthest column = the longest node path
  const longestPath = Math.max(...maxPathLengthMap.values());

  // the maximum number of row in a graph = the most number of nodes located within a single column
  const largestRow = maxOccurrences(maxPathLengthMap.values());

  return {
    nodeWidth: longestPath + 1,
    nodeHeight: largestRow,
  };
}

function getOwnerId(rowEntry: NodeMatrixEntry): ContainerNode['id'] | null {
  return rowEntry.allocation.owningId;
}

function isSnippetOwned(rowEntry: NodeMatrixEntry): boolean {
  const key = getOwnerId(rowEntry);
  return key ? isActionIdOfType(key, ActionType.SNIPPET) : false;
}

// function getAllocatedNodeId(entry: NodeMatrixEntry): SnippetNode['id'] | null {
//   return entry.allocation.allocatedNodeId;
// }

// function isSubGraphBoundary(a: NodeMatrixEntry, b: NodeMatrixEntry): boolean {
//   if (!a || !b) return false;
//   return getOwnerKey(a) !== getOwnerKey(b);
// }

// function isSnippetBoundary(a: NodeMatrixEntry, b: NodeMatrixEntry): boolean {
//   if (!isSubGraphBoundary(a, b)) return false;

//   const isASnippet = isActionIdOfType(getAllocatedNodeId(a), ActionType.SNIPPET);
//   const isBSnippet = isActionIdOfType(getAllocatedNodeId(b), ActionType.SNIPPET);

//   return isASnippet || isBSnippet;
// }

// function isCollapsedSnippetBoundary(a: WorkflowNode, b: WorkflowNode): boolean {
//   if (a && isCollapsedSnippetNode(a)) return true;
//   if (b && isCollapsedSnippetNode(b)) return true;

//   return false;
// }

/** Indicates if the next row iteration will be within a snippet subgraph. */
function isEnteringSnippetSubgraphYAxis(node?: WorkflowNode): boolean {
  // A snippet subgraph will always have an expanded snippet node as a leading column header.
  return isExpandedSnippetNode(node);
}

function isEnteringSnippetSubgraphXAxis(args: {
  rowEntry: NodeMatrixEntry;
  isInSnippetId: SnippetNode['id'] | null;
  node?: WorkflowNode;
}): boolean {
  const { rowEntry, isInSnippetId, node } = args;

  // we always reset the snippet id when we exit another snippet.
  return !isInSnippetId && isSnippetOwned(rowEntry) && !isSnippetNode(node);
}

function isExitingSnippetSubgraph(
  rowEntry: NodeMatrixEntry,
  isInSnippetId: SnippetNode['id'] | null,
): boolean {
  return isInSnippetId && (getOwnerId(rowEntry) !== isInSnippetId);
}

// the LHS of a snippet subgraph is offset by 8px
// we make sure that the inner columns within the graph absorb that offset
// so that the right most snippet internal child is aligned with external nodes
export function getSnippetColumnSpaceWidth(snippetDimensions: NodeDimensions) {
  return COLUMN_SPACING - (EXPANDED_SNIPPET_X_OFFSET / snippetDimensions.nodeWidth);
}

// Helper: shift a single column downward from a row index onward
function shiftColumnDown(matrix: NodeMatrix, colIndex: number, fromRow: number, delta: number) {
  const col = matrix[colIndex];
  for (let r = fromRow; r < col.length; r += 1) {
    col[r].position.y += delta;
  }
}

/**
 * Build spans for expanded snippet subgraphs by scanning the matrix.
 */
function deriveExpandedSnippetSpans(matrix: NodeMatrix): Record<SnippetNode['id'], SnippetSpan> {
  const spans: Record<SnippetNode['id'], SnippetSpan> = {};

  for (let colIndex = 0; colIndex < matrix.length; colIndex += 1) {
    const col = matrix[colIndex];

    for (let rowIndex = 0; rowIndex < col.length; rowIndex += 1) {
      const entry = col[rowIndex];

      // Only consider entries that belong to an expanded snippet subgraph
      const owner = getOwnerId(entry);
      // eslint-disable-next-line no-continue
      if (!owner) continue;

      const snippetId = owner;
      const existing = spans[snippetId];

      if (!existing) {
        spans[snippetId] = {
          snippetId,
          minCol: colIndex,
          maxCol: colIndex,
          minRow: rowIndex,
          maxRow: rowIndex,
        };
      } else {
        existing.minCol = Math.min(existing.minCol, colIndex);
        existing.maxCol = Math.max(existing.maxCol, colIndex);
        existing.minRow = Math.min(existing.minRow, rowIndex);
        existing.maxRow = Math.max(existing.maxRow, rowIndex);
      }
    }
  }

  return spans;
}

/**
 * Enforce alignment constraints across each expanded snippet span by pushing
 * "too-high" columns down to match the max Y at each row.
 */
function reconcileSnippetSpanYAlignment(args: {
  matrix: NodeMatrix;
  spans: SnippetSpan[];
}) {
  const { matrix, spans } = args;

  const maxIterations = 5;
  for (let iter = 0; iter < maxIterations; iter += 1) {
    let changed = false;

    for (const span of spans) {
      const {
        minCol,
        maxCol,
        minRow,
        maxRow,
      } = span;

      for (let rowIndex = minRow; rowIndex <= maxRow; rowIndex += 1) {
        // Compute target baseline Y for this row within the snippet's column span
        let targetY = -Infinity;
        for (let colIndex = minCol; colIndex <= maxCol; colIndex += 1) {
          const entry = matrix[colIndex]?.[rowIndex];
          // eslint-disable-next-line no-continue
          if (!entry) continue;
          targetY = Math.max(targetY, entry.position.y);
        }

        // eslint-disable-next-line no-continue
        if (!Number.isFinite(targetY)) continue;

        // Push down any columns that are above (smaller y) than the target
        for (let colIndex = minCol; colIndex <= maxCol; colIndex += 1) {
          const entry = matrix[colIndex]?.[rowIndex];
          // eslint-disable-next-line no-continue
          if (!entry) continue;

          const delta = targetY - entry.position.y;
          if (delta > 0.0001) {
            shiftColumnDown(matrix, colIndex, rowIndex, delta);
            changed = true;
          }
        }
      }
    }

    if (!changed) break;
  }
}


export function deriveXYPositions(args: {
  matrix: NodeMatrix;
  indexedNodes: Record<WorkflowNode['id'], WorkflowNode>;
  subGraphDimensions: Record<PublicId, NodeDimensions>;
  hasInputAttributeNodeFolder: boolean;
}) {
  const {
    matrix,
    indexedNodes,
    subGraphDimensions,
    hasInputAttributeNodeFolder,
  } = args;

  // PASS 1: gradually build Y by walking rows within each column
  matrix.forEach(column => {
    let y = CANVAS_VERTICAL_PADDING;
    let isInSnippetId: SnippetNode['id'] | null = null;

    column.forEach(row => {
      const node = indexedNodes[row.allocation.allocatedNodeId];

      // STEP 1: Y adjustments for THIS row

      // hoist XL nodes up slightly
      const isXLNode = isSnippetNode(node) || isRiskCalculationNode(node);
      if (isXLNode) {
        y -= XL_NODE_FIRST_ROW_HOIST;
      }

      if (isExitingSnippetSubgraph(row, isInSnippetId)) {
        y += EXPANDED_SNIPPET_Y_OFFSET;

        // only reset the next snippet id if we are traversing into another expanded snippet
        isInSnippetId = isExpandedSnippetNode(node)
          ? getOwnerId(row)
          : null;
      }

      // STEP 2: assign computed y for this matrix entry
      row.position.y = y;

      // STEP 3: Y adjustments for NEXT row
      const defaultSpacing = NODE_HEIGHT + ROW_SPACING;
      y += defaultSpacing;

      if (isCollapsedSnippetNode(node)) {
        y += EXPANDED_SNIPPET_Y_OFFSET;
      }

      if (isEnteringSnippetSubgraphYAxis(node)) {
        isInSnippetId = node.id;
        y += 8;
      }
    });
  });

  // PASS 2: Reconcile Y across expanded snippet regions
  // ---------------------------
  const spansMap = deriveExpandedSnippetSpans(matrix);

  reconcileSnippetSpanYAlignment({
    matrix,
    spans: Object.values(spansMap),
  });

  // PASS 3: set X by walking columns within each row
  const rowCount = matrix[0]?.length ?? 0;

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
    let x = CANVAS_HORIZONTAL_PADDING;
    let isInSnippetId: SnippetNode['id'] | null = null;

    matrix.forEach(column => {
      const entry = column[rowIndex];
      const node = indexedNodes[entry.allocation.allocatedNodeId];

      // STEP 1: X adjustments for THIS column
      if (isEnteringSnippetSubgraphXAxis({ rowEntry: entry, isInSnippetId, node })) {
        isInSnippetId = getOwnerId(entry);
        x += EXPANDED_SNIPPET_X_OFFSET;
      }


      // STEP 2: assign X position for this matrix entry
      entry.position.x = x;

      if (node && hasInputAttributeNodeFolder && isInputAttributeNode(node)) {
        entry.position = offsetFolderChildNode(entry.position);
      }

      // STEP 3: X adjustments for NEXT column

      if (isExitingSnippetSubgraph(entry, isInSnippetId)) {
        isInSnippetId = null;
      }

      const columnSpacing = isInSnippetId
        ? getSnippetColumnSpaceWidth(subGraphDimensions[isInSnippetId])
        : COLUMN_SPACING;

      const defaultSpacing = NODE_WIDTH + columnSpacing;
      x += defaultSpacing;
    });
  }
}

export function createDefaultNodeMatrix(dimensions: NodeDimensions): NodeMatrix {
  const { nodeWidth, nodeHeight } = dimensions;

  return Array.from({ length: nodeWidth }, () => (
    Array.from({ length: nodeHeight }, () => structuredClone(defaultNodeMatrixEntry))
  ));
}

function isReservedForNode(allocation: Allocation, node: WorkflowNode) {
  const { owningId, allocatedNodeId } = allocation;
  const layoutId = getLayoutContainerId(node);
  if (layoutId !== null) {
    return !allocatedNodeId && (owningId === layoutId);
  }
  return !allocatedNodeId && (owningId === node.id);
}

function isFree(allocation: Allocation) {
  const { owningId, allocatedNodeId } = allocation;
  return !owningId && !allocatedNodeId;
}

/**
 * Returns whether or not a space is free in the postion matrix.
 * The node position is the top-left corner of the space described by the supplied node dimensions.
 */
export function isSpaceFree(args: {
  matrix: NodeMatrix;
  position: NodePosition;
  dimensions: NodeDimensions;
}): boolean {
  const { matrix, position, dimensions } = args;

  const { column: startCol, row: startRow } = position;
  const { nodeWidth, nodeHeight } = dimensions;

  // Check all positions the node would occupy
  for (let col = startCol; col < startCol + nodeWidth; col += 1) {
    // out of bounds
    if (col >= matrix.length) return false;

    const rows = matrix[col];

    for (let row = startRow; row < startRow + nodeHeight; row += 1) {
      // out of bounds
      if (row >= rows.length) return false;

      // space is already occupied, space is not free
      if (rows[row].allocation.owningId) return false;
    }
  }

  return true;
}


/** Mutates the position matrix, marking the owning node id in each position */
function assignOwningNode(args: {
  matrix: NodeMatrix;
  position: NodePosition;
  dimensions: NodeDimensions;
  ownerId: WorkflowNode['id'];
}) {
  const {
    matrix,
    position,
    dimensions,
    ownerId,
  } = args;

  const { column: startCol, row: startRow } = position;
  const { nodeWidth, nodeHeight } = dimensions;

  const endCol = startCol + nodeWidth;
  const endRow = startRow + nodeHeight;

  for (let col = startCol; col < endCol; col += 1) {
    for (let row = startRow; row < endRow; row += 1) {
      matrix[col][row].allocation.owningId = ownerId;
    }
  }
}

/**
 * Get the column for a node.
 * Columns are determined by the largest dependent path a node might have. if a node has
 * no dependents, it is assigned column 0.
 */
export function getNodeColumn(
  maxPathLengthMap: MaxPathLengthMap,
  node: WorkflowNode,
): number {
  if (isSnippetInternalChildNode(node)) {
    if (maxPathLengthMap.has(node.id)) {
      // remove snippet parent action from column computation
      return maxPathLengthMap.get(node.id) - 1;
    }
    return 0;
  }
  return maxPathLengthMap.get(node.id) ?? 0;
}

/**
 * Creates a position matrix, marking subgraph spaces as owned by their container nodes.
 */
export function allocateSubgraphOwnership(args: {
  matrix: NodeMatrix;
  nodes: WorkflowNode[];
  maxIncomingPathLengthMap: MaxPathLengthMap;
  subGraphDimensions: Record<SnippetNode['id'], NodeDimensions>;
}): NodeMatrix {
  const {
    matrix,
    nodes,
    maxIncomingPathLengthMap,
    subGraphDimensions,
  } = args;

  // STEP 1: mark subgraph ownership within the node matrix
  nodes.forEach(node => {
    if (!isContainerNode(node)) {
      // only mark spaces owned by node containers
      return;
    }
    const dimensions = subGraphDimensions[node.id];
    const column = getNodeColumn(maxIncomingPathLengthMap, node);

    let row = 0;
    while (!isSpaceFree({ matrix, position: { column, row }, dimensions })) {
      row += 1;
    }

    assignOwningNode({
      matrix,
      position: { column, row },
      dimensions,
      ownerId: node.id,
    });
  });

  return matrix;
}

export function allocate(args: {
  matrix: NodeMatrix;
  position: NodePosition;
  dimensions: NodeDimensions;
  nodeId: WorkflowNode['id'];
}) {
  const {
    matrix,
    position,
    dimensions,
    nodeId,
  } = args;
  const { column, row } = position;

  const colEnd = column + dimensions.nodeWidth;
  const rowEnd = row + dimensions.nodeHeight;

  for (let rowIndex = row; rowIndex < rowEnd; rowIndex += 1) {
    for (let colIndex = column; colIndex < colEnd; colIndex += 1) {
      matrix[colIndex][rowIndex].allocation.allocatedNodeId = nodeId;
    }
  }
}

export function getSnippetNodeDimensions(
  subgraphDimensions: NodeDimensions,
): NodeDimensions {
  return { nodeWidth: subgraphDimensions.nodeWidth, nodeHeight: 1 };
}
